import { Link } from 'react-router-dom';
import { ArrowRight, Target, TrendingUp, Users, BarChart, DollarSign, Eye } from 'lucide-react';
import { PageSEO } from '@/components/SEO';

const fordeler = [
  {
    icon: Target,
    title: 'Målrettet annonsering',
    description: 'Vi treffer akkurat de kundene du ønsker å nå med presise målgrupper.',
  },
  {
    icon: TrendingUp,
    title: 'Målbar ROI',
    description: 'Full oversikt over resultater og avkastning på annonsekronene.',
  },
  {
    icon: Users,
    title: 'Remarketing',
    description: 'Nå ut til besøkende som allerede har vist interesse for deg.',
  },
  {
    icon: BarChart,
    title: 'A/B-testing',
    description: 'Kontinuerlig testing og optimalisering for bedre resultater.',
  },
  {
    icon: DollarSign,
    title: 'Budsjettoptimalisering',
    description: 'Få mest mulig ut av annonsebudsjettet ditt.',
  },
  {
    icon: Eye,
    title: 'Transparent rapportering',
    description: 'Månedlige rapporter med full innsikt i resultater.',
  },
];

const plattformer = [
  { name: 'Google Ads', description: 'Søkeannonsering og displaynettverk' },
  { name: 'Facebook Ads', description: 'Annonsering på Facebook og Instagram' },
  { name: 'LinkedIn Ads', description: 'B2B-annonsering for bedriftsmarkedet' },
  { name: 'YouTube Ads', description: 'Videoannonsering for merkevarebygging' },
];

export default function Annonsering() {
  return (
    <div>
      <PageSEO title="Digital Annonsering – Google Ads & Sosiale Medier" path="/tjenester/annonsering" description="Målrettet annonsering på Google, Facebook og Instagram for norske bedrifter. Vi sikrer at dine annonsekroner gir faktisk avkastning. Kontakt oss for analyse." />
      {/* Hero */}
      <section className="pt-16 md:pt-20 pb-16 md:pb-20 bg-muted" data-testid="annonsering-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="font-manrope text-xs font-medium tracking-[0.3em] uppercase text-accent mb-4">
                Tjeneste
              </p>
              <h1 className="font-syne font-bold text-5xl md:text-6xl text-primary tracking-tight leading-[0.95]">
                Digital annonsering som gir avkastning
              </h1>
              <p className="font-manrope text-lg text-muted-foreground mt-8 leading-relaxed">
                Nå ut til personer som aktivt søker etter produktene eller tjenestene du tilbyr. Vi hjelper deg med målrettet annonsering på Google, Facebook, Instagram og LinkedIn.
              </p>
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 font-syne font-bold text-sm uppercase tracking-wide px-8 py-4 bg-primary text-primary-foreground hover:bg-accent transition-colors duration-300 group mt-8"
                data-testid="annonsering-cta"
              >
                Få en gratis analyse
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            </div>
            <div className="relative group rounded-3xl overflow-hidden shadow-2xl ring-1 ring-primary/5">
              <div className="absolute -top-4 -right-4 w-full h-full border-2 border-accent/20 rounded-3xl" />
              <img
                src="https://images.unsplash.com/photo-1553877522-43269d4ea984?auto=format&fit=crop&q=80"
                alt="Digital annonsering og markedsføring i hele Norge"
                className="relative z-10 w-full h-[400px] object-cover transition-transform duration-700 group-hover:scale-[1.03]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Plattformer */}
      <section className="py-24 md:py-32" data-testid="annonsering-plattformer">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-2xl mb-16">
            <p className="font-manrope text-xs font-medium tracking-[0.3em] uppercase text-accent mb-4">
              Plattformer
            </p>
            <h2 className="font-syne font-bold text-4xl md:text-5xl text-primary tracking-tight">
              Vi annonserer der kundene dine er
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {plattformer.map((plattform, index) => (
              <div
                key={index}
                className="p-8 border border-border rounded-2xl hover:border-accent/50 transition-all duration-500 hover:shadow-lg flex items-start gap-6 group"
                data-testid={`plattform-${index}`}
              >
                <span className="font-syne font-bold text-4xl text-accent">0{index + 1}</span>
                <div>
                  <h3 className="font-syne font-bold text-xl text-primary mb-2">
                    {plattform.name}
                  </h3>
                  <p className="font-manrope text-sm text-muted-foreground">
                    {plattform.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Fordeler */}
      <section className="py-24 md:py-32 bg-primary text-primary-foreground" data-testid="annonsering-fordeler">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-2xl mb-16">
            <p className="font-manrope text-xs font-medium tracking-[0.3em] uppercase text-accent mb-4">
              Fordeler
            </p>
            <h2 className="font-syne font-bold text-4xl md:text-5xl tracking-tight">
              Hvorfor velge oss for annonsering?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {fordeler.map((fordel, index) => (
              <div
                key={index}
                className="p-8 border border-primary-foreground/10 rounded-2xl hover:border-accent/50 transition-all duration-500 hover:shadow-lg group"
                data-testid={`annonsering-fordel-${index}`}
              >
                <fordel.icon className="w-10 h-10 text-accent mb-6" />
                <h3 className="font-syne font-bold text-lg mb-3">
                  {fordel.title}
                </h3>
                <p className="font-manrope text-sm text-primary-foreground/70 leading-relaxed">
                  {fordel.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-muted" data-testid="annonsering-cta-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-syne font-bold text-4xl md:text-5xl text-primary tracking-tight mb-6">
            Klar for å nå flere kunder?
          </h2>
          <p className="font-manrope text-muted-foreground max-w-xl mx-auto mb-10">
            Få en gratis gjennomgang av mulighetene for digital annonsering for din bedrift.
          </p>
          <Link
            to="/kontakt"
            className="inline-flex items-center gap-2 font-syne font-bold text-sm uppercase tracking-wide px-8 py-4 bg-primary text-primary-foreground hover:bg-accent transition-colors duration-300 group"
            data-testid="annonsering-bottom-cta"
          >
            Kontakt oss
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
        </div>
      </section>
    </div>
  );
}
